/**
 * Fields that know what they hold.
 *
 * The app had 150 inputs and 106 of them validated nothing, because every
 * screen wired its own formatting and checking by hand — so a name
 * capitalised on one screen and not the next, and a license formatted when
 * added but not when edited. Every one of those was a separate bug and every
 * fix was a separate patch.
 *
 * A field here carries its own rules. <NameField> capitalises and validates
 * because it is a name, not because the screen remembered to ask. Adding a
 * name to a new screen cannot produce the old bug.
 *
 * Each takes `required` and reports through `onValid`, so a form can gather
 * its errors without repeating the rules.
 */

import { useState } from "react";
import { Field, Select, PhoneField, MoneyField } from "./ui";
import {
  checkRequired, checkEmail, checkZip, checkDateOfBirth,
  capitalizeName, formatEin, stripEin, checkEin, normalizeTag, checkTag,
} from "../lib/validation";
import { formatFloridaLicense, checkFloridaLicense } from "../lib/license";
import { US_STATES, COUNTRIES } from "../lib/places";

interface Base {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  hint?: string;
  /** Shown before the field has been touched; suppressed until then. */
  error?: string | null;
  /** Extra work on blur — a duplicate check, a lookup. The field's own
      validation still runs; this is in addition to it, not instead. */
  onBlur?: () => void;
}

/** Checks on blur rather than on every keystroke: complaining that an email
    is invalid while it is still being typed is noise, not help. */
function useTouched(check: (v: string) => string | null, error?: string | null) {
  const [touched, setTouched] = useState(false);
  const [own, setOwn] = useState<string | null>(null);
  return {
    shown: error ?? (touched ? own : null),
    onBlur: (v: string) => { setTouched(true); setOwn(check(v)); },
    revalidate: (v: string) => { if (touched) setOwn(check(v)); },
  };
}

/* ------------------------------------------------------------------ */

/** A person's name. Capitalises as it is typed. */
export function NameField({ label = "Name", value, onChange, required, hint, error }: Base) {
  const check = (v: string) => (required ? checkRequired(v, label) : null);
  const t = useTouched(check, error);
  return (
    <Field label={label} value={value} hint={hint} error={t.shown}
           autoComplete="off"
           onChange={(v) => { const c = capitalizeName(v); onChange(c); t.revalidate(c); }}
           onBlur={() => t.onBlur(value)} />
  );
}

/** Any plain text that is not a name — a company, an employer, a note. */
export function TextField({ label = "", value, onChange, required, hint, error, onBlur }: Base) {
  const check = (v: string) => (required ? checkRequired(v, label) : null);
  const t = useTouched(check, error);
  return (
    <Field label={label} value={value} hint={hint} error={t.shown}
           onChange={(v) => { onChange(v); t.revalidate(v); }}
           onBlur={() => { t.onBlur(value); onBlur?.(); }} />
  );
}

export function EmailField({ label = "Email", value, onChange, required, hint, error }: Base) {
  const check = (v: string) =>
    (required ? checkRequired(v, label) : null) ?? checkEmail(v);
  const t = useTouched(check, error);
  return (
    <Field label={label} type="email" inputMode="email" value={value}
           hint={hint} error={t.shown} autoComplete="email"
           onChange={(v) => { onChange(v.trim()); t.revalidate(v); }}
           onBlur={() => t.onBlur(value)} />
  );
}

export function ZipField({ label = "ZIP", value, onChange, required, hint, error }: Base) {
  const check = (v: string) =>
    (required ? checkRequired(v, label) : null) ?? checkZip(v);
  const t = useTouched(check, error);
  return (
    <Field label={label} inputMode="numeric" maxLength={10} value={value}
           hint={hint} error={t.shown}
           // Digits and one hyphen: a ZIP+4 is legal, letters are not.
           onChange={(v) => {
             const c = v.replace(/[^0-9-]/g, "").slice(0, 10);
             onChange(c); t.revalidate(c);
           }}
           onBlur={() => t.onBlur(value)} />
  );
}

export function DateOfBirthField({ label = "Date of birth", value, onChange, required, hint, error }: Base) {
  const check = (v: string) =>
    (required ? checkRequired(v, label) : null) ?? checkDateOfBirth(v);
  const t = useTouched(check, error);
  return (
    <Field label={label} type="date" value={value} hint={hint} error={t.shown}
           onChange={(v) => { onChange(v); t.revalidate(v); }}
           onBlur={() => t.onBlur(value)} />
  );
}

/** A date that cannot be in the future — a purchase, a payment, a signing. */
export function PastDateField({ label = "Date", value, onChange, required, hint, error }: Base) {
  const check = (v: string) => {
    const req = required ? checkRequired(v, label) : null;
    if (req || !v) return req;
    return v > new Date().toISOString().slice(0, 10)
      ? `${label} cannot be in the future.` : null;
  };
  const t = useTouched(check, error);
  return (
    <Field label={label} type="date" value={value} hint={hint} error={t.shown}
           max={new Date().toISOString().slice(0, 10)}
           onChange={(v) => { onChange(v); t.revalidate(v); }}
           onBlur={() => t.onBlur(value)} />
  );
}

/**
 * A date something is promised by.
 *
 * Deliberately permissive about the past: a we-owe can be entered a week
 * after the appointment was meant to happen, and refusing that date would
 * only push someone into leaving it blank. It warns instead, which is the
 * useful signal — a promise already past its date is the one to chase.
 */
export function DueDateField({
  label = "Promised by", value, onChange, required, hint, error, min,
}: Base & {
  /** Floor for the picker. Left off by default, because a we-owe is often
      entered after the date it was meant to happen and refusing that would
      only push somebody into leaving it blank. A promise to pay is the other
      case: a date already gone gets no reminder, so it is worth stopping. */
  min?: string;
}) {
  const check = (v: string) => {
    const req = required ? checkRequired(v, label) : null;
    if (req || !v) return req;
    return null;
  };
  const t = useTouched(check, error);
  const past = value && value < new Date().toISOString().slice(0, 10);
  return (
    <Field label={label} type="date" value={value} min={min}
           hint={past ? "That date has already passed." : (hint ?? "Optional")}
           error={t.shown}
           onChange={(v) => { onChange(v); t.revalidate(v); }}
           onBlur={() => t.onBlur(value)} />
  );
}

/* ------------------------------------------------------------------ */
/* Identification                                                      */
/* ------------------------------------------------------------------ */

export function LicenseNumberField({
  label = "Number", value, onChange, required, hint, error, idType, idState,
}: Base & { idType: string; idState: string }) {
  const check = (v: string) =>
    (required ? checkRequired(v, label) : null)
    ?? checkFloridaLicense(v, idState, idType);
  const t = useTouched(check, error);
  const formats = idType !== "passport" && idState.trim().toUpperCase() === "FL";
  return (
    <Field label={label} mono value={value} hint={hint} error={t.shown}
           onChange={(v) => {
             const c = formats ? formatFloridaLicense(v) : v;
             onChange(c); t.revalidate(c);
           }}
           onBlur={() => t.onBlur(value)} />
  );
}

export function StateSelect({ label = "State", value, onChange, required, hint, error }: Base) {
  return (
    <Select label={label} value={value} onChange={onChange} hint={hint}
            error={error ?? (required && !value ? `${label} is required.` : null)}>
      <option value="">Choose</option>
      {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
    </Select>
  );
}

export function CountrySelect({ label = "Country", value, onChange, required, hint, error }: Base) {
  return (
    <Select label={label} value={value} onChange={onChange} hint={hint}
            error={error ?? (required && !value ? `${label} is required.` : null)}>
      <option value="">Choose</option>
      {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
    </Select>
  );
}

/* ------------------------------------------------------------------ */
/* Business and vehicle                                                */
/* ------------------------------------------------------------------ */

export function EinField({ label = "EIN", value, onChange, required, hint, error }: Base) {
  const check = (v: string) => (required || v ? checkEin(v) : null);
  const t = useTouched(check, error);
  return (
    <Field label={label} mono value={formatEin(value)}
           hint={hint ?? "00-0000000"} error={t.shown}
           onChange={(v) => { const c = stripEin(v); onChange(c); t.revalidate(c); }}
           onBlur={() => t.onBlur(value)} />
  );
}

export function TagField({ label = "Tag number", value, onChange, hint, error }: Base) {
  const t = useTouched(checkTag, error);
  return (
    <Field label={label} mono value={value} hint={hint} error={t.shown}
           onChange={(v) => { const c = normalizeTag(v); onChange(c); t.revalidate(c); }}
           onBlur={() => t.onBlur(value)} />
  );
}

/** Whole miles, no decimals, no commas going in. */
export function OdometerField({ label = "Mileage", value, onChange, required, hint, error }: Base) {
  const check = (v: string) => {
    const req = required ? checkRequired(v, label) : null;
    if (req || !v) return req;
    const n = Number(v.replace(/\D/g, ""));
    if (!Number.isFinite(n) || n <= 0) return "Enter the mileage.";
    if (n > 2_000_000) return "That is more miles than a car does.";
    return null;
  };
  const t = useTouched(check, error);
  return (
    <Field label={label} inputMode="numeric" value={value} hint={hint} error={t.shown}
           onChange={(v) => {
             const c = v.replace(/\D/g, "").slice(0, 7);
             onChange(c); t.revalidate(c);
           }}
           onBlur={() => t.onBlur(value)} />
  );
}

/* Already correct in ui.tsx, re-exported so every field is reached for in one
   place. A screen importing half its fields from one module and half from
   another is how the standard slips again. */
export { PhoneField, MoneyField };

/** The five-digit code printed on a Florida insurance card. */
export function InsuranceCodeField({ label = "Company code", value, onChange, hint, error }: Base) {
  const check = (v: string) =>
    !v ? null : (/^\d{5}$/.test(v) ? null : `That code is five digits — this has ${v.length}.`);
  const t = useTouched(check, error);
  return (
    <Field label={label} inputMode="numeric" maxLength={5} value={value}
           hint={hint ?? "Five digits, on the insurance card"} error={t.shown}
           onChange={(v) => {
             const c = v.replace(/\D/g, "").slice(0, 5);
             onChange(c); t.revalidate(c);
           }}
           onBlur={() => t.onBlur(value)} />
  );
}

/** An expiry date. Optional, but a document that expired last year is worth
    knowing about before it reaches the paperwork. */
export function ExpiryField({ label = "Expires", value, onChange, hint, error }: Base) {
  const check = (v: string) =>
    !v ? null
      : v < new Date().toISOString().slice(0, 10) ? "That date has passed." : null;
  const t = useTouched(check, error);
  return (
    <Field label={label} type="date" value={value} hint={hint ?? "Optional"}
           error={t.shown}
           onChange={(v) => { onChange(v); t.revalidate(v); }}
           onBlur={() => t.onBlur(value)} />
  );
}
